/** Split product title from size/color options for cart/checkout BEM rows. */

export type CartLineDisplay = {
  title: string;
  options: string[];
};

const OPTION_SEP = /\s·\s/;

/**
 * Prefer explicit `options` when present. Otherwise parse legacy names like
 * `Hoodie · M · Pink` into title + option chips.
 */
export function splitCartLineName(name: string, options?: string[] | null): CartLineDisplay {
  const explicit = (options || []).map((o) => String(o || '').trim()).filter(Boolean);
  const raw = (name || '').trim();
  if (explicit.length) {
    return { title: raw || 'Item', options: explicit };
  }
  if (!raw) return { title: 'Item', options: [] };
  const parts = raw.split(OPTION_SEP).map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) return { title: raw, options: [] };
  return { title: parts[0], options: parts.slice(1) };
}

/** Cart / Warenkorb page slugs (not checkout). */
export function isCartPageSlug(slug: string): boolean {
  return /^(cart|warenkorb|basket)$/i.test((slug || '').trim());
}

/** Dedicated checkout / Kasse page slugs. */
export function isCheckoutPageSlug(slug: string): boolean {
  return /^(checkout|kasse)$/i.test((slug || '').trim());
}

export function isCheckoutNavLink(label: string, url: string): boolean {
  const l = (label || '').trim().toLowerCase();
  const u = (url || '').trim().toLowerCase();
  if (/^(checkout|kasse|zur\s*kasse|place\s*order|bestellung)$/i.test(l)) return true;
  if (/(checkout|kasse)/i.test(l) && l.length < 28) return true;
  return /\/(checkout|kasse)(\/|$|\?|#)/i.test(u);
}

type PageLike = { slug: string };

/** Prefer cart/warenkorb/basket; never treat checkout-only pages as the cart. */
export function resolveCartPageSlug(pages: PageLike[] | undefined): string | null {
  const list = pages || [];
  const hit = list.find((p) => isCartPageSlug(p.slug));
  return hit ? hit.slug : null;
}

/** Prefer dedicated checkout/kasse; else null (caller may fall back to cart + #checkout). */
export function resolveCheckoutPageSlug(pages: PageLike[] | undefined): string | null {
  const list = pages || [];
  const hit = list.find((p) => isCheckoutPageSlug(p.slug));
  return hit ? hit.slug : null;
}

/**
 * Checkout target: dedicated page, else cart page with `#checkout` anchor,
 * else `/checkout`.
 */
export function resolveCheckoutHref(opts: {
  pages?: PageLike[];
  cartHref?: string;
  checkoutNavHref?: string;
}): string {
  const nav = (opts.checkoutNavHref || '').trim();
  if (nav) return nav;

  const checkoutSlug = resolveCheckoutPageSlug(opts.pages);
  if (checkoutSlug) return `/${checkoutSlug}`;

  const cartSlug = resolveCartPageSlug(opts.pages);
  if (cartSlug) return `/${cartSlug}#checkout`;

  const cartHref = (opts.cartHref || '').trim();
  if (cartHref) {
    if (cartHref.includes('#')) return cartHref;
    return `${cartHref}#checkout`;
  }

  return '/checkout';
}

/** Combined cart + checkout on one page: which step the hash asks for. */
export type CartCheckoutStep = 'cart' | 'checkout';

/**
 * When a page hosts both `cart-summary` and `checkout-form`, only one step
 * should be visible. `#checkout` / `#kasse` → checkout; otherwise cart.
 * Returns null when the page is not a dual-step commerce page.
 */
export function resolveCartCheckoutStep(opts: {
  components?: Array<{ type?: string } | null | undefined>;
  hash?: string;
}): CartCheckoutStep | null {
  const list = opts.components || [];
  const hasCart = list.some((c) => c?.type === 'cart-summary');
  const hasCheckout = list.some((c) => c?.type === 'checkout-form');
  if (!hasCart || !hasCheckout) return null;
  const h = String(opts.hash || '').replace(/^#/, '').trim().toLowerCase();
  if (h === 'checkout' || h === 'kasse') return 'checkout';
  return 'cart';
}

/** Hide the inactive step (and payment promos during checkout) on dual-step pages. */
export function shouldHideBlockForCartCheckoutStep(
  blockType: string,
  step: CartCheckoutStep | null,
): boolean {
  if (!step) return false;
  if (step === 'cart') return blockType === 'checkout-form';
  return blockType === 'cart-summary' || blockType === 'payment-gateway';
}
